import { CURRENT_VERSION } from '~/data/version'
import { apiFetch } from '~/utils/api'
import { IS_NATIVE, PLATFORM } from '~/utils/platform'

export const CLIENT_CONFIG_TIMEOUT_MS = 5000

// Replace the iOS placeholder once the App Store listing has an ID.
export const NATIVE_STORE_URL = PLATFORM === 'android'
  ? 'market://details?id=com.elementaldispatch.game'
  : 'https://apps.apple.com/app/id0000000000'

/** Versions are integer pairs: v7.10 follows v7.9, and v8.0 follows v7.99. */
export function isVersionOlder(current: string, floor: unknown): boolean {
  const version = /^v(\d+)\.(\d+)$/
  const have = version.exec(current)
  const need = typeof floor === 'string' ? version.exec(floor) : null
  if (!have || !need) return false
  const [major, minor] = [Number(have[1]), Number(have[2])]
  const [minMajor, minMinor] = [Number(need[1]), Number(need[2])]
  if (![major, minor, minMajor, minMinor].every(Number.isSafeInteger)) return false
  return major! < minMajor! || (major === minMajor && minor! < minMinor!)
}

/** Public startup check: no Firebase identity or App Check token is needed.
 * A bad response, unavailable network, or five-second deadline fails open.
 * Race the request as well as aborting it, so even a stalled transport cannot
 * hold startup or a retry indefinitely. A previously confirmed floor is kept
 * on an inconclusive retry; only a live configuration can release that screen.
 * Late answers never change the result. */
export async function checkClientVersion(current = CURRENT_VERSION, unavailable: string | null = null): Promise<string | null> {
  if (!IS_NATIVE) return null
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => {
      resolve(undefined)
      controller.abort()
    }, CLIENT_CONFIG_TIMEOUT_MS)
  })
  try {
    const request = apiFetch<{ minVersion?: Partial<Record<'android' | 'ios', unknown>> }>('/api/client-config', {
      method: 'GET',
      signal: controller.signal,
      retry: 0,
    })
    const config = await Promise.race([request, deadline])
    const floor = config?.minVersion?.[PLATFORM as 'android' | 'ios']
    if (floor === null) return null // a live server explicitly removed the floor
    if (typeof floor !== 'string' || !/^v\d+\.\d+$/.test(floor)) return unavailable
    return isVersionOlder(current, floor) ? floor : null
  } catch {
    return unavailable
  } finally {
    clearTimeout(timer)
  }
}
